'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { KolInsightsMarketSection } from '@flipster/features/src/kol-insights/market-section';
import { SegmentPage } from '@flipster/features/src/misc/segment/segment-page';
import { CopyTradingSection } from '@flipster/features/src/shared/copy-trading/section';
import { EntryPopupPageSetter } from '@flipster/features/src/shared/entry-popup';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { useThrottleCallback } from '@flipster/react/misc/throttle';
import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import { on } from '@flipster/utils/dom-event-listener';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { CategoryCarousel } from './carousel/category-carousel';
import { TopTraderCarousel } from './carousel/top-trader-carousel';
import { MarketDiscoverTabHeightVariableKey, MarketTabHeightVariableKey } from './constants';
import { MarketDiscover } from './market-discover';
import { MarketTab, type MarketTabRef } from './market-tab';
import { MarketTrend } from './market-trend';
import { MarketPageTabContext, marketPageBunja, marketSymbolBunja, marketTabListBunja } from './state';
import { TradFiPromoBanner } from './tradfi-promo-banner';
import { Sort, TabValueType } from './types';
import { ZeroSpread } from './zero-spread';

interface Props {
  productType: 'perpetual' | 'spot';
}

export const MarketPage = ({ productType }: Props) => {
  return (
    <MarketPageTabContext value={productType}>
      <MarketPageImpl />
    </MarketPageTabContext>
  );
};

const MarketPageImpl = () => {
  const {
    refs: { discoverTabRef, discoverAnchorRef, marketTabRef },
    styleVariables,
    scrollToList,
  } = useCalcMarketStyleVariables();
  const {
    tab: marketTab,
    setDiscoverCursorTab,
    setDiscoverCursorSort,
    setCursorPage,
    searchModeAtom,
  } = useBunja(marketPageBunja);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  const searchParams = useSearchParams();
  const isMounted = useMounted();
  const { refetchSymbolsByMarketTab, isLoadingAtom } = useBunja(marketSymbolBunja);
  const isSymbolsLoading = useAtomValue(isLoadingAtom);
  const didInitDiscoverTabRef = useRef(false);
  const { tabListAtom } = useBunja(marketTabListBunja);
  const tabList = useAtomValue(tabListAtom);
  const tabListRef = useRef(tabList);
  tabListRef.current = tabList;
  const isSearchMode = useAtomValue(searchModeAtom);

  const onClickTabCategory = (t: TabValueType, s?: Sort) => {
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.set('tab', t);
    nextParams.delete('page');
    window.history.replaceState(null, '', `?${nextParams}`);
    setOrderAttribution(OrderAttributionMapper.market.discover[t]);
    setDiscoverCursorTab(t);
    if (s) setDiscoverCursorSort({ sortKey: s.sortKey, sortOrder: s.sortOrder });
    refetchSymbolsByMarketTab();
    requestAnimationFrame(() => scrollToList());
  };

  useEffect(() => {
    if (didInitDiscoverTabRef.current || isSymbolsLoading) return;
    didInitDiscoverTabRef.current = true;

    const tabParam = searchParams.get('tab');
    if (tabParam && tabListRef.current.some((tab) => tab.value === tabParam)) {
      setDiscoverCursorTab(tabParam as TabValueType);
      const pageParam = Number(searchParams.get('page'));
      if (pageParam > 0) setCursorPage(pageParam);
      return;
    }

    if (marketTab === 'spot') {
      setDiscoverCursorTab('marketCap');
      return;
    }
    const hasTradFiTab = tabListRef.current.some((tab) => tab.value === 'tradfi');
    setDiscoverCursorTab(hasTradFiTab ? 'tradfi' : 'marketCap');
  }, [isSymbolsLoading, marketTab, setDiscoverCursorTab, setCursorPage, searchParams]);

  if (!isMounted) {
    return (
      <main className={center(layoutRawCss)}>
        <SpinnerBox />
      </main>
    );
  }

  return (
    <>
      <SegmentPage name={MainEvent.MarketPageView} />
      <EntryPopupPageSetter page='WEB_MARKET' />

      <main className={css(layoutRawCss)} style={styleVariables}>
        <MarketTab ref={marketTabRef} scrollToList={scrollToList} />
        {!isSearchMode && (
          <>
            <MarketTrend onChangeTab={onClickTabCategory} />
            {marketTab === 'perpetual' && (
              <>
                <div className={css({ pb: { base: 32, tablet: 48 } })}>
                  <CategoryCarousel onChangeTab={onClickTabCategory} />
                  <TradFiPromoBanner />
                </div>
                <KolInsightsMarketSection css={{ px: { base: 16, tablet: 0 }, pb: { base: 32, tablet: 48 } }} />
                <CopyTradingSection css={{ px: { base: 16, tablet: 0 }, pb: { base: 32, tablet: 48 } }} />
                <TopTraderCarousel />
              </>
            )}
          </>
        )}
        <MarketDiscover
          ref={discoverTabRef}
          anchorRef={discoverAnchorRef}
          onChangeTab={onClickTabCategory}
          scrollToList={scrollToList}
        />
        {!isSearchMode && marketTab === 'perpetual' && <ZeroSpread />}
      </main>
    </>
  );
};

const layoutRawCss = css.raw({
  minH: 'minHeightWithoutNavigation',
  padding: '30px 0 56px',
  display: 'flex',
  flexDirection: 'column',
  tablet: {
    display: 'block',
    maxWidth: 'desktopMaxWidth',
    boxSizing: 'content-box',
    padding: '80px 16px 150px',
    margin: '0 auto',
  },
});

function createStyleObject(heights: Record<string, number>) {
  return {
    [MarketTabHeightVariableKey]: `${heights.marketTab}px`,
    [MarketDiscoverTabHeightVariableKey]: `${heights.discoverTab}px`,
  };
}

function useCalcMarketStyleVariables() {
  const isMounted = useMounted();
  const discoverTabRef = useRef<HTMLDivElement>(null);
  const discoverAnchorRef = useRef<HTMLDivElement>(null);
  const marketTabRef = useRef<MarketTabRef>(null);
  const [styleVariables, setStyleVariables] = useState<Record<string, string>>();
  const measure = useCallback(() => {
    setStyleVariables(
      createStyleObject({
        discoverTab: discoverTabRef.current?.getBoundingClientRect().height ?? 0,
        marketTab: marketTabRef.current?.getClientHeight() ?? 0,
      }),
    );
  }, []);
  const throttledMeasure = useThrottleCallback(measure, 100);
  useEffect(() => {
    if (!isMounted) return;
    measure();
    const offResize = on(window, 'resize', throttledMeasure);
    const resizeObserver = new ResizeObserver(() => measure());
    if (discoverTabRef.current) resizeObserver.observe(discoverTabRef.current);
    return () => {
      offResize?.();
      resizeObserver.disconnect();
    };
  }, [measure, throttledMeasure, isMounted]);
  const scrollToList = useCallback(() => {
    discoverAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return { refs: { discoverTabRef, discoverAnchorRef, marketTabRef }, styleVariables, scrollToList };
}
